import {
  ChargerModel,
  ChargerModelDeployment,
  ChargerModelUpdate,
} from '../stores/types/chargerModels.interface';
import { API_KEYS, Api } from './base.api';
import { SCOPE_TYPE } from './utils';

export interface ChargerModelImageUploadUrlResponse {
  uploadUrl: string;
  publicUrl: string;
}

/** Delay before refetching the deployment list so DELETE has time to propagate (avoids stale rows). */
const CHARGER_MODEL_LIST_REFETCH_AFTER_DELETE_MS = 400;

export const chargerModelsApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    fetchChargerModelById: builder.query<ChargerModel, string>({
      keepUnusedDataFor: 0,
      query: (id) => ({
        url: `/internal/core/v2/chargerModels/${id}`,
        method: 'GET',
        needCompanyId: false,
        params: {
          scope: SCOPE_TYPE.NONE,
        },
      }),
      transformResponse: (response: ChargerModel) => response,
    }),
    fetchChargerModelDeployment: builder.query<ChargerModelDeployment[], void>({
      query: () => ({
        url: `/internal/core/v2/chargerModels`,
        method: 'GET',
      }),
      providesTags: () => [{ type: API_KEYS.CHARGER_MODEL_DEPLOYMENT, id: 'LIST' }],
    }),
    fetchChargerModelImageUploadUrl: builder.query<
      ChargerModelImageUploadUrlResponse,
      { filename: string; contentType?: string }
    >({
      query: ({ filename, contentType }) => ({
        url: `internal/charger-model-image-upload`,
        params: {
          filename,
          ...(contentType ? { contentType } : {}),
        },
      }),
    }),
    deleteChargerModel: builder.mutation<void, string>({
      query: (id) => ({
        url: `/internal/core/v2/chargerModels/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [{ type: API_KEYS.CHARGER_MODELS, id }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        await new Promise((resolve) =>
          setTimeout(resolve, CHARGER_MODEL_LIST_REFETCH_AFTER_DELETE_MS),
        );
        try {
          await dispatch(
            chargerModelsApi.endpoints.fetchChargerModelDeployment.initiate(undefined, {
              forceRefetch: true,
            }),
          ).unwrap();
        } catch {
          /* list refetch failed */
        }
      },
    }),
    updateChargerModel: builder.mutation<ChargerModel, ChargerModelUpdate>({
      query: (body) => ({
        url: `/internal/core/v2/chargerModels/${body.id}`,
        method: 'PATCH',
        body,
        needCompanyId: false,
        params: {
          scope: SCOPE_TYPE.NONE,
        },
      }),
      invalidatesTags: (_r, _e, body) => [{ type: API_KEYS.CHARGER_MODEL_DEPLOYMENT, id: 'LIST' }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        try {
          await dispatch(
            chargerModelsApi.endpoints.fetchChargerModelDeployment.initiate(undefined, {
              forceRefetch: true,
            }),
          ).unwrap();
        } catch {
          /* list refetch failed */
        }
      },
    }),
    createChargerModel: builder.mutation<ChargerModel, ChargerModelUpdate>({
      query: (body) => ({
        url: `/internal/core/v2/chargerModels`,
        method: 'POST',
        body,
        needCompanyId: false,
        params: {
          scope: SCOPE_TYPE.NONE,
        },
      }),
      invalidatesTags: (result) =>
        result ? [{ type: API_KEYS.CHARGER_MODEL_DEPLOYMENT, id: 'LIST' }] : [],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        try {
          await dispatch(
            chargerModelsApi.endpoints.fetchChargerModelDeployment.initiate(undefined, {
              forceRefetch: true,
            }),
          ).unwrap();
        } catch {
          /* list refetch failed */
        }
      },
    }),
  }),
});

export const {
  useFetchChargerModelByIdQuery,
  useFetchChargerModelDeploymentQuery,
  useLazyFetchChargerModelImageUploadUrlQuery,
  useDeleteChargerModelMutation,
  useUpdateChargerModelMutation,
  useCreateChargerModelMutation,
} = chargerModelsApi;
